import {call, put, takeLatest} from 'redux-saga/effects'
import {PayloadAction} from '@reduxjs/toolkit'
import {HTTP_RESPONSE_STATUS} from 'setup/constants/Http'
import {ToastDefaultConfig} from 'setup/toast/ToastConfig'
import {toast} from 'react-toastify'
import {districtApi} from './DistrictApi'
import {districtActions} from './DistrictSlice'
import {DistrictApiDto, DistrictOption, normalizeDistricts} from '../models/District'
import {ResponseArray} from 'setup/axios/HttpResponse'

/** Envelope may use data: T[] or nested lists (districts/rows/items). */
function extractDistrictDtoList(response: any): DistrictApiDto[] {
  const d = response?.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object') {
    if (Array.isArray(d.districts)) return d.districts
    if (Array.isArray(d.rows)) return d.rows
    if (Array.isArray(d.items)) return d.items
  }
  if (Array.isArray(response)) return response as DistrictApiDto[]
  return []
}

/** Map API province field to the exact key used in Redux (requested codes). */
function resolveProvinceBucketKey(raw: DistrictApiDto, requested: string[]): string | undefined {
  const v = raw?.provinceCode ?? raw.provinceId
  if (v === undefined || v === null) return undefined
  const pc = String(v).trim()
  if (!pc) return undefined

  if (requested.includes(pc)) return pc

  for (const r of requested) {
    const rs = String(r).trim()
    if (rs === pc) return r
  }

  const numPc = Number(pc)
  if (!Number.isNaN(numPc)) {
    for (const r of requested) {
      const nr = Number(String(r).trim())
      if (!Number.isNaN(nr) && nr === numPc) return r
    }
  }

  return undefined
}

function buildDistrictsByProvince(
  provinceCodes: string[],
  rawList: DistrictApiDto[],
  normalized: DistrictOption[]
): Record<string, DistrictOption[]> {
  const byProvince: Record<string, DistrictOption[]> = {}
  provinceCodes.forEach((c) => {
    byProvince[c] = []
  })

  const orphans: DistrictOption[] = []

  rawList.forEach((raw, i) => {
    const n = normalized[i]
    if (!n) return
    const key = resolveProvinceBucketKey(raw, provinceCodes)
    if (key && byProvince[key] !== undefined) {
      byProvince[key].push(n)
    } else {
      orphans.push(n)
    }
  })

  const allBucketsEmpty = provinceCodes.every((c) => !byProvince[c]?.length)

  if (allBucketsEmpty && normalized.length > 0) {
    if (provinceCodes.length === 1) {
      byProvince[provinceCodes[0]] = normalized
    } else {
      // No row matched requested keys (missing/wrong province fields): expose same list under each key; UI dedupes.
      provinceCodes.forEach((c) => {
        byProvince[c] = normalized.slice()
      })
    }
  } else if (orphans.length > 0 && provinceCodes.length === 1) {
    byProvince[provinceCodes[0]].push(...orphans)
  }

  return byProvince
}

function* fetchDistricts(action: PayloadAction<string[]>): Generator<any, void, any> {
  const rawCodes = action.payload ?? []
  const provinceCodes = Array.from(new Set(rawCodes.map(String).map((c) => c.trim()).filter(Boolean)))
  if (provinceCodes.length === 0) {
    return
  }

  try {
    const response: ResponseArray<DistrictApiDto> = yield call(
      districtApi.getDistrictsByProvinceCodes,
      provinceCodes
    )

    if (response.status === HTTP_RESPONSE_STATUS.SUCCESS) {
      const rawList = extractDistrictDtoList(response)
      const normalized = normalizeDistricts({data: rawList})
      const byProvince = buildDistrictsByProvince(provinceCodes, rawList, normalized)
      yield put(districtActions.fetchDistrictsSuccess({byProvince}))
    } else {
      const errorMessage = response.message || 'Failed to load districts'
      toast.error(errorMessage, ToastDefaultConfig())
      yield put(
        districtActions.fetchDistrictsFailed({
          requestedCodes: provinceCodes,
          error: errorMessage,
        })
      )
    }
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to load districts'
    toast.error(errorMessage, ToastDefaultConfig())
    yield put(
      districtActions.fetchDistrictsFailed({
        requestedCodes: provinceCodes,
        error: errorMessage,
      })
    )
  }
}

export function* districtSaga() {
  yield takeLatest(districtActions.fetchDistrictsStart.type, fetchDistricts)
}
